import {HttpResponse} from '../../interfaces/api/http';
import {api} from '../api';
import http from '../http';
import {ChatType, TabIds} from '../../utils/enums';
import Config from 'react-native-config';

export interface GetChatsForm {
  start?: number;
  length?: number;
  search?: string;
  favourite?: number;
  tab_id?: string;
  type: ChatType;
}

export interface GetFavoritesForm {
  page?: number;
  pageSize?: number;
  type: string;
  thread_type?: ChatType;
  tab_id?: string;
}

export interface FavoritesResponse {
  data: ChatItemResponse[];
  total: number;
  pageSize: number;
  page: number;
}

export interface ChatItemResponse {
  id: number;
  title: string;
  user_id: number;
  type: string;
  category_id?: number;
  category?: string;
  created_at: string;
  is_favorite: boolean;
  favorite_id?: number;
  layout?: 'ltr' | 'rtl';
  [key: string]: any;
}

/**
 * Chat management services
 * Provides methods for retrieving, managing, and manipulating chat history
 */
export const chats = {
  /**
   * Retrieves a list of chats with optional filtering
   * @param {GetChatsForm} [body] - Optional parameters for filtering chats
   * @returns {Promise<GetChatsResponse>} Promise resolving to chat list response
   */
  getChats: (body?: GetChatsForm): Promise<HttpResponse<ChatItemResponse[]>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.threads}?search=${body?.search || ''}&is_favorite=${body?.favourite || 0}${body?.tab_id && +body?.tab_id !== TabIds.QA ? `&tab_id=${body.tab_id}` : ''}&type=${body?.tab_id && +body?.tab_id === TabIds.QA ? ChatType.QNA : ChatType.ClinicalCase}&date=${Number(new Date())}`;
      http
        .get<ChatItemResponse[]>(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Retrieves a list of favorite chats with pagination
   * @param {GetFavoritesForm} [body] - Optional parameters for filtering favorites
   * @returns {Promise<HttpResponse<FavoritesResponse>>} Promise resolving to favorites list response
   */
  getFavorites: (body?: GetFavoritesForm): Promise<HttpResponse<FavoritesResponse>> =>
    new Promise((resolve, reject) => {
      let url = `/favorite?type=${body?.type || 'Chat'}&page=${body?.page || 1}&pageSize=${body?.pageSize || 10}`;
      if (body?.thread_type) {
        url += `&thread_type=${body.thread_type}`;
      }
      if (body?.tab_id) {
        url += `&tab_id=${body.tab_id}`;
      }
      http
        .get<FavoritesResponse>(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Adds a chat to favorites
   * @param {number} modelId - ID of the chat to add to favorites
   * @param {string} [tab_id] - Optional tab ID to associate with the favorite
   * @returns {Promise<HttpResponse>} Promise resolving to the API response
   */
  addFavorite: (modelId: number, tab_id?: string): Promise<HttpResponse<any>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.addFavorite}`;
      const body: any = {
        model_id: modelId,
        type: Config.SHARE_FAVORITE_TYPE || 'Thread',
      };
      if (tab_id) {
        body.tab_id = tab_id;
      }
      http
        .post<any>(url, body)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Removes a chat from favorites
   * @param {number} favoriteId - ID of the favorite to remove
   * @returns {Promise<HttpResponse>} Promise resolving to the API response
   */
  removeFavorite: (favoriteId: number): Promise<HttpResponse<any>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.removeFavorite}/${favoriteId}`;
      http
        .post<any>(url, {
          type: Config.SHARE_FAVORITE_TYPE || 'Thread',
        })
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),

  /**
   * Deletes a chat from history
   * @param {number} chatId - ID of the chat to remove
   * @returns {Promise<HttpResponse>} Promise resolving to the API response
   */
  removeChat: (chatId: number): Promise<HttpResponse<ChatItemResponse[]>> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.removeChat}/${chatId}`;
      http
        .delete<ChatItemResponse[]>(url)
        .then(res => resolve(res))
        .catch(e => reject(e));
    }),
};
